import { useState, useEffect } from 'react'
import { MerchantLayout } from '../../components/layouts/Layout'
import { useAuth } from '../../hooks/useAuth'
import { Card, Button, Badge, Modal } from '../../components/common/UI'
import { Plus, Eye, MousePointer, Calendar, Target, AlertCircle, ShieldCheck } from 'lucide-react'
import { formatters } from '../../utils/formatting'
import { advertisementService, businessService } from '../../services/api'
import { useNavigate } from 'react-router-dom'

export default function Advertisements() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [advertisements, setAdvertisements] = useState<any[]>([])
  const [businesses, setBusinesses] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedBusiness, setSelectedBusiness] = useState('')

  useEffect(() => {
    if (user?.merchantId) {
      loadData()
    }
  }, [user?.merchantId])

  const loadData = async () => {
    if (!user?.merchantId) return
    
    try {
      const [ads, biz] = await Promise.all([
        advertisementService.listByMerchant(user.merchantId),
        businessService.listByMerchant(user.merchantId)
      ])
      setAdvertisements(ads)
      setBusinesses(biz)
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
    }
  }

  const verifiedBusinesses = businesses.filter(b => 
    b.verificationStatus === 'VERIFIED' && 
    b.status === 'ACTIVE'
  )

  const handleCreateAd = () => {
    if (businesses.length === 0) {
      alert('You need to create at least one business before creating advertisements.')
      navigate('/merchant/businesses')
      return
    }

    if (verifiedBusinesses.length === 0) {
      alert('You need at least one VERIFIED and ACTIVE business to create advertisements. Please wait for admin approval of your business.')
      return
    }
    setShowCreateModal(true)
  }

  const handleBusinessSelect = (businessId: string) => {
    setSelectedBusiness(businessId)
  }

  const handleProceed = () => {
    if (!selectedBusiness) {
      alert('Please select a business')
      return
    }
    
    const business = verifiedBusinesses.find(b => b.id === selectedBusiness)
    if (!business) {
      alert('Selected business is not verified. Please select a verified business.')
      return
    }

    if (business.verificationStatus !== 'VERIFIED') {
      alert(`This business is ${business.verificationStatus}. Only VERIFIED businesses can create advertisements.`)
      return
    }

    if (business.status !== 'ACTIVE') {
      alert(`This business is ${business.status}. Only ACTIVE businesses can create advertisements.`)
      return
    }

    navigate(`/merchant/advertisements/create?businessId=${selectedBusiness}`)
  }

  if (loading) {
    return (
      <MerchantLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      </MerchantLayout>
    )
  }

  return (
    <MerchantLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Advertisements</h1>
            <p className="text-gray-600">Manage your ad campaigns</p>
          </div>
          <Button onClick={handleCreateAd} icon={Plus} disabled={verifiedBusinesses.length === 0}>
            Create Ad
          </Button>
        </div>

        {businesses.length === 0 && (
          <Card className="bg-yellow-50 border-yellow-200">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900">No Businesses Found</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Please create a business before creating advertisements.
                </p>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate('/merchant/businesses')}>
                  Go to Businesses
                </Button>
              </div>
            </div>
          </Card>
        )}

        {businesses.length > 0 && verifiedBusinesses.length === 0 && (
          <Card className="bg-yellow-50 border-yellow-200">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900">Business Verification Required</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Your business must be VERIFIED and ACTIVE before you can create advertisements. 
                  Please wait for admin approval.
                </p>
                <div className="mt-3 space-y-2">
                  {businesses.map(business => (
                    <div key={business.id} className="flex items-center justify-between bg-white rounded p-2">
                      <span className="text-sm font-medium">{business.businessName}</span>
                      <Badge variant={
                        business.verificationStatus === 'VERIFIED' ? 'success' :
                        business.verificationStatus === 'PENDING' ? 'warning' : 'error'
                      }>
                        {business.verificationStatus}
                      </Badge>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate('/merchant/businesses')}>
                  View Businesses
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {advertisements.map((ad) => (
            <Card key={ad.id} className="flex flex-col hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{ad.adTitle}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">{ad.adContent}</p>
                </div>
                <Badge variant={
                  ad.approvalStatus === 'APPROVED' ? 'success' :
                  ad.approvalStatus === 'PENDING' ? 'warning' : 'error'
                }>
                  {ad.approvalStatus}
                </Badge>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">Impressions</span>
                  </div>
                  <span className="font-semibold">{formatters.number(ad.impressionCount || 0)}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <MousePointer className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">Clicks</span>
                  </div>
                  <span className="font-semibold">{formatters.number(ad.clickCount || 0)}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <Target className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">CTR</span>
                  </div>
                  <span className="font-semibold">
                    {ad.impressionCount > 0 
                      ? ((ad.clickCount / ad.impressionCount) * 100).toFixed(2) 
                      : '0.00'}%
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">Period</span>
                  </div>
                  <span className="text-xs">
                    {formatters.date(ad.startDate)} - {formatters.date(ad.endDate)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-xs text-gray-500">
                  {ad.isActive ? '🟢 Active' : '⚪ Inactive'}
                </span>
                <Button size="sm" variant="outline">View Details</Button>
              </div>
            </Card>
          ))}
        </div>

        {advertisements.length === 0 && verifiedBusinesses.length > 0 && (
          <Card className="text-center py-12">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Advertisements Yet</h3>
            <p className="text-gray-600 mb-4">Create your first ad campaign to reach customers</p>
            <Button onClick={handleCreateAd} icon={Plus}>Create Ad</Button>
          </Card>
        )}

        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Select Business for Advertisement"
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Choose which verified business this advertisement will be for. Only VERIFIED and ACTIVE businesses are shown.
            </p>

            <div className="space-y-2">
              {verifiedBusinesses.map((business) => (
                <button
                  key={business.id}
                  onClick={() => handleBusinessSelect(business.id)}
                  className={`w-full p-4 border rounded-lg text-left transition-all ${
                    selectedBusiness === business.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-semibold">{business.businessName}</h4>
                        <div title="Verified">
                          <ShieldCheck className="w-4 h-4 text-green-600" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {Array.isArray(business.businessType) 
                          ? business.businessType.join(', ') 
                          : business.businessType}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant="success" size="sm">VERIFIED</Badge>
                        <Badge variant="success" size="sm">ACTIVE</Badge>
                      </div>
                    </div>
                    {selectedBusiness === business.id && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {verifiedBusinesses.length === 0 && (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  No verified businesses available. Please wait for admin approval.
                </p>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleProceed} disabled={!selectedBusiness || verifiedBusinesses.length === 0} className="flex-1">
                Continue
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </MerchantLayout>
  )
}